import type { EquipmentManual } from '@/types';

// FCC 装置主要设备的操作手册库——内容贴合炼厂催化裂化现场实际工艺
export const fccManuals: Record<string, EquipmentManual> = {
  'P-101': {
    overview:
      '原料油泵 P-101 是 FCC 装置的入口泵，负责将原料罐区的减压蜡油/常渣升压送入换热器及加热炉，' +
      '是装置进料量稳定与提升管反应负荷控制的关键设备。',
    operatingProcedure: [
      '启动前先打开机械密封冷却水及轴承箱冷却水，确认回水畅通',
      '手动盘车 2-3 圈，确认转子无卡涩、无异常摩擦',
      '检查泵体油位，确认润滑油油位在视镜中线偏上',
      '关闭出口阀、微开入口阀进行灌泵排气，待出口压力表稳定',
      '点动启动主电机，观察转向是否与箭头一致',
      '正式启动后缓慢打开出口阀至工艺所需流量，避免水击',
      '运行稳定 5 分钟后投入流量自动控制（FIC）',
      '记录出入口压力、电流、轴承温度，纳入交接班表',
    ],
    safetyNotes: [
      '严禁在出口阀全关状态下长时间运行，防止憋压超温',
      '机封漏油超过 3 滴/分钟需立即停泵处理，防止着火',
      '盘车与启动前必须挂"禁止合闸"警示牌，防误操作',
      '冬季停泵后须排净泵腔余液，防止冻裂',
    ],
    troubleshooting: [
      { symptom: '出口压力低、流量下降', action: '检查入口过滤器压差，必要时切换备用泵' },
      { symptom: '泵体振动大', action: '停泵盘车检查叶轮，排查轴承磨损或对中偏差' },
      { symptom: '电流偏高', action: '核对介质粘度与温度，检查出口阀开度是否过大' },
    ],
  },
  'E-101': {
    overview:
      '原料油换热器 E-101 利用分馏塔回流热源对冷原料进行预热，回收装置自身余热，' +
      '降低加热炉负荷，是装置能耗优化的关键管壳式换热设备。',
    operatingProcedure: [
      '投用前确认管程、壳程放空阀关闭，排凝阀关闭',
      '先通冷流（原料油）建立通路，再缓慢引入热流',
      '控制冷热介质升温速率不大于 30°C/h，防止热应力开裂',
      '观察出入口温度差与压差，确认换热效果在设计范围',
      '逐步提量至工艺额定流量，投自动控制',
      '运行中每 2 小时巡检一次法兰密封、放空与压力表',
    ],
    safetyNotes: [
      '严禁带压拆卸法兰螺栓，必须先泄压、置换、确认无压',
      '冷热介质投用顺序不能颠倒，避免设备过热',
      '高温侧外保温脱落须及时报修，防止人员烫伤',
    ],
    troubleshooting: [
      { symptom: '换热效率下降、出口温度偏低', action: '检查管束结垢情况，安排在线/离线清洗' },
      { symptom: '管壳程压差升高', action: '排查介质带杂质或管束堵塞，必要时切除清洗' },
      { symptom: '法兰渗漏', action: '降负荷后均匀紧固螺栓，仍漏需停车更换垫片' },
    ],
  },
  'F-101': {
    overview:
      '加热炉 F-101 将换热后的原料油进一步加热到反应所需温度（约 360-400°C），' +
      '为提升管反应器提供稳定的进料温度，是 FCC 装置的能量"心脏"。',
    operatingProcedure: [
      '点炉前用蒸汽对炉膛吹扫不少于 15 分钟，确认无可燃气体残留',
      '检查长明灯/点火枪、燃料气压力是否在 0.05-0.15 MPa 范围',
      '先点长明灯，再逐个引燃主火嘴，避免回火',
      '炉膛升温速率控制不超过 50°C/h，防止炉管热冲击',
      '稳定运行后投用炉出口温度自动控制（TIC）',
      '巡检关注炉管颜色、火焰形状、烟气氧含量',
      '联锁试验完好率每月不少于一次',
    ],
    safetyNotes: [
      '点火前必须确认炉膛吹扫合格，否则严禁打火',
      '燃料气压力低于联锁值时立即切断，禁止强投',
      '炉管出现局部红斑/弯曲必须立即降量并联系检修',
      '现场严禁使用非防爆通讯设备',
    ],
    troubleshooting: [
      { symptom: '炉出口温度上不去', action: '检查燃料气压力与雾化蒸汽，调整火嘴' },
      { symptom: '炉膛冒黑烟', action: '加大配风或降低燃料气量，调风油比' },
      { symptom: '炉管局部超温', action: '立即降负荷，平衡火嘴，必要时切除该火嘴' },
    ],
  },
  'R-101': {
    overview:
      '提升管反应器 R-101 是 FCC 装置的核心反应设备，原料油在再生剂带入热量的作用下' +
      '发生催化裂化反应，生成轻质油气产品。反应温度通常控制在 480-520°C，剂油比 5-8。',
    operatingProcedure: [
      '开车前对反应器系统进行氮气置换，氧含量低于 0.5% 方可进油',
      '先建立催化剂循环，再缓慢引入原料油',
      '反应温度通过再生滑阀开度调节，控制在 480-520°C',
      '油气比维持在 5-8，过低会降低转化率，过高会增加焦炭',
      '反应压力维持在 0.22-0.28 MPa，与再生器联动控制',
      '维持沉降器藏量稳定，防止反应器跑剂',
      '关注汽提段蒸汽用量，确保催化剂汽提彻底',
    ],
    safetyNotes: [
      '严禁在反应器带油状态下检修，防止油气泄漏着火',
      '反应温度超过 540°C 立即投急冷油并降量',
      '催化剂跑剂报警须立即处理，防止下游堵塞',
      '沉降器旋分入口压差超限时必须降量',
    ],
    troubleshooting: [
      { symptom: '反应温度异常升高', action: '关小再生滑阀，投急冷油，必要时降量' },
      { symptom: '剂油比偏低、产品质量差', action: '提高催化剂循环量或降低进料量' },
      { symptom: '反应压力波动大', action: '排查两器差压控制及主风、富气压缩机负荷' },
    ],
  },
  'REG-101': {
    overview:
      '再生器 REG-101 通过通入主风将待生剂上的焦炭烧掉，恢复催化剂活性并提供反应所需热量，' +
      '再生温度 680-700°C，是装置热平衡与剂耗控制的关键设备。',
    operatingProcedure: [
      '开车前用主风顶置换再生器至氧含量合格',
      '点火升温采用辅助燃烧室，升温速率不大于 30°C/h',
      '建立再生—反应催化剂循环，控制两器差压 0.02-0.05 MPa',
      '主风量按烧焦负荷调整，烟气氧含量维持 1-3%',
      '再生温度控制在 680-700°C，过高易催化剂烧结',
      '密相藏量按设计中位运行，防止跑剂或欠藏',
      '定期取样测定碳含量，再生剂含碳应低于 0.1%',
    ],
    safetyNotes: [
      '严禁主风中断时仍向再生器送剂，防止后燃失控',
      '烟气 CO 含量异常升高必须立即降负荷查明原因',
      '再生器尾燃（密相—稀相温差>30°C）必须紧急处理',
      '人孔检修前必须置换合格并办理受限空间作业票',
    ],
    troubleshooting: [
      { symptom: '再生温度持续升高', action: '减少进料、降低生焦量，检查烧焦平衡' },
      { symptom: '再生压力下降', action: '检查主风量与主风机出口流量，排查过滤器堵塞' },
      { symptom: '催化剂跑剂', action: '检查旋分系统与料腿翼阀，调整密相藏量' },
    ],
  },
  'T-101': {
    overview:
      '分馏塔 T-101 将反应器来的高温油气按沸点切割成富气、汽油、柴油、回炼油及油浆等产品，' +
      '塔顶温度 120-130°C，塔顶压力 0.10-0.15 MPa，是产品分离的关键设备。',
    operatingProcedure: [
      '开车前用蒸汽对塔体进行预热，温度不低于 100°C',
      '先建立顶循、一中、二中循环，再引入反应油气',
      '逐步建立各侧线产品采出，按质量调整侧线流量',
      '塔顶温度通过顶循回流量进行控制（TIC）',
      '塔底液位维持在 40-60%，防止液泛或抽空',
      '塔底吹汽量按设计量给入，过大会导致冲塔',
      '关注各塔盘压差，发现异常及时处理',
    ],
    safetyNotes: [
      '严禁塔顶系统进水，防止水击造成塔盘损坏',
      '塔顶压力超 0.20 MPa 立即处理，防止安全阀起跳',
      '检修前必须氮气置换并取样合格',
      '塔顶冷凝器跳车时立即减少塔顶气相负荷',
    ],
    troubleshooting: [
      { symptom: '塔顶温度高、产品变重', action: '增加顶循回流量，核查反应深度' },
      { symptom: '冲塔（塔顶温度突升）', action: '降塔底液位、减吹汽，加大顶循回流' },
      { symptom: '侧线产品不合格', action: '调整侧线采出量与中段回流量' },
    ],
  },
  'K-101': {
    overview:
      '主风机 K-101 是再生器的烧焦风源，提供 1500-1800 Nm³/min 的高压空气，' +
      '通常采用轴流式压缩机+蒸汽透平驱动，是装置能量与烧焦平衡的关键转动设备。',
    operatingProcedure: [
      '启动前确认润滑油、控制油系统投用，油温 35-45°C',
      '盘车检查无异常，盘车装置脱开后方可启动',
      '透平暖管充分，蒸汽参数符合启动要求',
      '低速暖机 30 分钟，逐步升至额定转速',
      '出口防喘振阀按曲线自动控制，禁止手动长时间打开',
      '运行中关注轴位移、振动、轴承温度三大参数',
      '出口风量按再生烧焦需求调整',
    ],
    safetyNotes: [
      '严禁机组在喘振区运行，听到喘振声立即降负荷',
      '轴位移超 0.5 mm 必须紧急停机',
      '入口过滤器压差报警必须及时处理，防止喘振',
      '联锁保护一律不得擅自摘除',
    ],
    troubleshooting: [
      { symptom: '出口风量下降', action: '检查入口过滤器压差，切换备用过滤器' },
      { symptom: '机组振动升高', action: '检查转子平衡及轴承状态，必要时停机' },
      { symptom: '出现喘振', action: '立即开防喘振阀，降转速，排查工况' },
    ],
  },
  'V-101': {
    overview:
      '塞阀 V-101 是再生器与提升管之间的催化剂循环控制阀，通过调节开度控制再生剂下料量，' +
      '是反应温度的主要调节手段，开度通常在 50-80% 区间运行。',
    operatingProcedure: [
      '投用前确认仪表风、伴热蒸汽正常',
      '由现场配合 DCS 缓慢开启，观察阀位反馈与流量响应',
      '开度调节须缓慢平稳，单次变动不超过 5%',
      '与反应温度联锁投用前进行回路测试',
      '阀位与流量出现明显非线性时及时联系仪表处理',
    ],
    safetyNotes: [
      '严禁在催化剂循环建立前打开塞阀，防止反向流动',
      '阀杆密封漏剂必须立即处理，防止跑剂烫伤',
      '检修前必须切断介质并冷却至 60°C 以下',
    ],
    troubleshooting: [
      { symptom: '阀位调不动/卡阀', action: '检查阀杆是否结焦，必要时投用密封蒸汽冲洗' },
      { symptom: '反应温度波动大', action: '检查阀位反馈，排查仪表风及定位器问题' },
      { symptom: '阀门内漏', action: '停车后拆检阀芯磨损情况' },
    ],
  },
  'PI-101': {
    overview:
      '反应压力表 PI-101 监控提升管反应器顶部压力，正常 0.22-0.28 MPa，' +
      '是反应—再生两器压力平衡与联锁保护的重要测量仪表。',
    operatingProcedure: [
      '投用前对取压管线进行吹扫，确认畅通无堵',
      '打开根部阀，缓慢开启仪表前截止阀',
      '观察就地表与 DCS 显示是否一致，偏差大于 1% 需校验',
      '定期（每周）对取压管线进行反吹',
      '定期（每年）送计量校验，校验合格证粘贴于现场',
    ],
    safetyNotes: [
      '严禁在投用状态下拆装表头，必须先关根部阀泄压',
      '取压管线伴热不得停用，防止冬季冻堵',
      '指针抖动剧烈应立即检查，避免误判工况',
    ],
    troubleshooting: [
      { symptom: '示值偏低或无指示', action: '检查取压管线是否堵塞，进行反吹' },
      { symptom: '就地表与 DCS 偏差大', action: '联系仪表班校验或更换' },
      { symptom: '表头渗漏', action: '关根部阀泄压后更换密封件或表头' },
    ],
  },
};
